import { useTopicGenerationStatusContext } from "@/contexts/TopicGenerationStatusContext";
import { Loader2, Sparkles, MessageSquare, BookOpen, Search, ChevronRight, Activity } from "lucide-react";
import { cn } from "@/lib/utils";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";

interface TopicGenerationStatusProps {
    className?: string;
    variant?: "pill" | "icon" | "persistent";
}

export const TopicGenerationStatus = ({ className, variant = "pill" }: TopicGenerationStatusProps) => {
    const { streamingPhase, loading, slug, sessionId, topic } = useTopicGenerationStatusContext();
    const navigate = useNavigate();

    if (!streamingPhase && !loading) return null;

    const getStatusContent = () => {
        switch (streamingPhase) {
            case "topics":
                return {
                    icon: <Search className="w-3 h-3 animate-pulse" />,
                    text: "Generating Topics...",
                    color: "text-blue-500",
                    bgColor: "bg-blue-500/10",
                };
            case "generating-all":
                return {
                    icon: <Sparkles className="w-3 h-3 animate-pulse" />,
                    text: "Generating All Content...",
                    color: "text-purple-500",
                    bgColor: "bg-purple-500/10",
                };
            case "questions":
                return {
                    icon: <MessageSquare className="w-3 h-3 animate-pulse" />,
                    text: "Creating Questions...",
                    color: "text-blue-500",
                    bgColor: "bg-blue-500/10",
                };
            case "vocabulary":
                return {
                    icon: <BookOpen className="w-3 h-3 animate-pulse" />,
                    text: "Building Vocabulary...",
                    color: "text-green-500",
                    bgColor: "bg-green-500/10",
                };
            case "speaking-guide":
                return {
                    icon: <Sparkles className="w-3 h-3 animate-pulse" />,
                    text: "Generating Speaking Guide...",
                    color: "text-amber-500",
                    bgColor: "bg-amber-500/10",
                };
            default:
                return {
                    icon: <Loader2 className="w-3 h-3 animate-spin" />,
                    text: "AI Working...",
                    color: "text-primary",
                    bgColor: "bg-primary/10",
                };
        }
    };

    const { icon, text, color, bgColor } = getStatusContent();

    const handleNavigate = () => {
        if (sessionId) {
            navigate(`/my-topics/edit/${sessionId}`);
        } else if (slug) {
            navigate(`/session/${slug}/practice`);
        }
    };

    if (variant === "icon") {
        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        className={cn(
                            "flex items-center justify-center w-8 h-8 rounded-full border border-current/20 animate-in fade-in zoom-in duration-300 cursor-pointer hover:bg-current/10 transition-colors",
                            bgColor,
                            color,
                            className
                        )}
                        aria-label={text}
                    >
                        {icon}
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[280px]">
                    <DropdownMenuLabel className="flex items-center gap-2">
                        <Activity className="h-4 w-4" />
                        AI Generation Status
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <div className="p-4 space-y-4">
                        <div className="flex items-center gap-3">
                            <div className={cn("p-2 rounded-lg", bgColor)}>
                                {icon}
                            </div>
                            <div>
                                <p className="text-sm font-semibold">{text}</p>
                                {streamingPhase === "generating-all" && (
                                    <p className="text-xs text-muted-foreground">Questions, Vocabulary & Speaking Guide</p>
                                )}
                            </div>
                        </div>

                        {streamingPhase === "generating-all" ? (
                            <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                                <div className="h-full w-1/2 bg-gradient-to-r from-transparent via-purple-500 to-transparent animate-shimmer" />
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <div className="flex items-center justify-between text-xs">
                                    <span className="text-muted-foreground">Progress</span>
                                    <span className="font-medium">
                                        {streamingPhase === "topics" ? "0%" :
                                            streamingPhase === "questions" ? "25%" :
                                                streamingPhase === "vocabulary" ? "50%" :
                                                    streamingPhase === "speaking-guide" ? "75%" : "100%"}
                                    </span>
                                </div>
                                <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden">
                                    <div
                                        className={cn("h-full transition-all duration-500", color.replace('text-', 'bg-'))}
                                        style={{
                                            width: streamingPhase === "topics" ? "10%" :
                                                streamingPhase === "questions" ? "30%" :
                                                    streamingPhase === "vocabulary" ? "60%" :
                                                        streamingPhase === "speaking-guide" ? "90%" : "100%"
                                        }}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                    {(sessionId || slug) && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={handleNavigate} className="cursor-pointer text-primary">
                                <Activity className="h-4 w-4 mr-2" />
                                {sessionId ? "Go to Topic Detail" : "Go to Practice Session"}
                                <ChevronRight className="h-4 w-4 ml-auto" />
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        );
    }

    if (variant === "persistent") {
        return (
            <button
                onClick={handleNavigate}
                className={cn(
                    "group flex items-center gap-1.5 h-9 max-w-[220px] sm:max-w-[280px] px-2.5 rounded-full border border-current/20 text-xs font-medium animate-in fade-in slide-in-from-top duration-300 cursor-pointer hover:bg-current/15 transition-colors",
                    bgColor,
                    color,
                    className
                )}
                aria-label={topic ? `${text}: ${topic}` : text}
                title={topic || undefined}
            >
                {icon}
                <span className="flex items-center gap-1.5 min-w-0">
                    <span className="shrink-0">{text}</span>
                    {topic && (
                        <>
                            <span className="shrink-0 opacity-60">·</span>
                            <span className="truncate font-semibold">{topic}</span>
                        </>
                    )}
                </span>
            </button>
        );
    }

    return (
        <div
            onClick={handleNavigate}
            className={cn(
                "flex items-center gap-2 px-3 py-1 rounded-full border border-current/20 text-xs font-medium animate-in fade-in zoom-in duration-300 cursor-pointer hover:opacity-80 transition-opacity",
                bgColor,
                color,
                className
            )}
        >
            {icon}
            <span>{text}</span>
        </div>
    );
};
